import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppSelector } from '../../../../../../../app/hooks';
import { fetchAskAicaFileBlob } from '../../../../../../../store/dashboard/askAica/apiHelper';
import {
    addFileToReports,
    addOriginalXlsxToReports,
    addEditedFileToReports,
    ADD_TO_REPORTS_FAILED_MESSAGE,
} from '../../../../../../../common/Reports/addFileToReports';
import type { AddToWorkspacePayload } from '../../../../../../../common/Reports/addFileToReports';
import { isEditedWorkspaceSnapshot } from '../../../../../../../common/Reports/editedWorkspaceSnapshot';
import { showAddedToReportsToast } from '../../../../../../../common/Reports/addedToReportsToast';
import { useAddToReportsVisible } from '../../../../../../../common/Reports/useAddToReportsVisible';
import {
    countFilledCells,
    importFileToSheets,
    isReadOnlyAddBand,
    isReadOnlySize,
    sheetLoadErrorMessage,
} from '../../../../../../../common/SheetEditor';
import type { FortuneSheet } from '../../../../../../../common/SheetEditor';
import {
    EXT_MIME,
    DOCX_TYPES,
    SPREADSHEET_TYPES,
    openBlobViewer,
    closeBlobViewer,
} from '../../../../../../../common/DocumentViewer/constants';
import type { BlobViewerState } from '../../../../../../../common/DocumentViewer/constants';

/** A generated Ask AICA file, as referenced by a FileAttachmentCard (chat or Library). */
export interface AskAicaFileRef {
    url: string;
    name: string;
    type?: string;
}

/** Closes all three generated-file drawer hosts (docx/sheet/file) at once — shared
 *  by every mount surface that also owns a competing drawer host (e.g. citation
 *  drawers) and needs to release this one before opening the other. */
export const closeGeneratedFileDrawers = (viewers: {
    docxViewer: { onClose: () => void } | null;
    sheetViewer: { onClose: () => void } | null;
    fileViewer: { onClose: () => void } | null;
}) => {
    viewers.docxViewer?.onClose();
    viewers.sheetViewer?.onClose();
    viewers.fileViewer?.onClose();
};

interface DocxViewerHostState {
    file: AskAicaFileRef;
    blob: Blob;
}

interface SheetViewerHostState {
    file: AskAicaFileRef;
    sheets: FortuneSheet[];
    /** The fetched file's bytes, kept ONLY for a read-only-band workbook (too
     *  many filled cells to edit) — its Download / Add store these as-is. */
    originalBytes?: ArrayBuffer;
}

interface FileViewerHostState extends BlobViewerState {
    file: AskAicaFileRef;
    mimeType: string;
}

/** Resolve a routable MIME type: filename extension first (authoritative,
 *  matches every other viewer-routing site in this repo), else the file's own
 *  `type` field, else fall back to pdf (DocumentViewer's own default branch
 *  for anything unmatched). */
const resolveMimeType = (file: AskAicaFileRef): string => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return EXT_MIME[ext] ?? file.type ?? EXT_MIME.pdf;
};

const triggerBlobDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/** Downloads a blob that isn't otherwise tracked in component state (e.g. docxViewer,
 *  which only holds the Blob, not a persistent object URL) — creates a throwaway
 *  object URL, triggers the download, then revokes it immediately. */
const triggerBlobDownloadOnce = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    triggerBlobDownload(url, fileName);
    URL.revokeObjectURL(url);
};

/**
 * Owns the Ask AICA "open a file" and "add to Reports" behaviour: pre-fetches
 * the file as an authenticated Blob (Athena's `file_url` is a relative,
 * auth-gated path — a plain fetch inside the viewers would 401), then routes
 * to the matching viewer (DocxViewer / SheetEditorDrawer / FileViewerDrawer)
 * or snapshots it into Reports via the shared `addFileToReports` util.
 * Never passes the raw Athena fileUrl to a viewer — only the pre-fetched
 * blob/blob-URL.
 */
/**
 * @param overrideOrgId When supplied, wins over the route param — used by the
 * portfolio surface (no route `:id`; the org is the gate-resolved
 * currentCompany.org_id instead). Leads passes nothing, unchanged behaviour.
 */
export const useAskAicaFileActions = (overrideOrgId?: string) => {
    const { id: routeOrgId } = useParams();
    const contextOrgId = overrideOrgId ?? routeOrgId;
    const userData = useAppSelector((state) => state.user.userData);
    const canAddToReports = useAddToReportsVisible();

    const [docxViewer, setDocxViewer] = useState<DocxViewerHostState | null>(null);
    const [sheetViewer, setSheetViewer] = useState<SheetViewerHostState | null>(null);
    const [fileViewer, setFileViewer] = useState<FileViewerHostState | null>(null);
    const [addingToReports, setAddingToReports] = useState(false);
    const addingToReportsRef = useRef(false);
    const [addedToReports, setAddedToReports] = useState(false);
    // Which file (by url) is mid-open. A sheet/docx open does an authenticated
    // fetch + (for sheets) a worker parse before the viewer mounts with in-memory
    // content, so without this the clicked card sits dead for seconds. Drives the
    // card's inline spinner.
    const [openingFileUrl, setOpeningFileUrl] = useState<string | null>(null);
    // Bumped by `invalidatePendingOpens` (called on AskAica close). `openFile`
    // captures the generation at call time and re-checks it after every await —
    // a fetch/parse that resolves after the modal has closed must not mount a
    // viewer or leave openingFileUrl stuck, since the viewer drawers render
    // outside the modal and would pop up after the user already left.
    const openGenerationRef = useRef(0);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const closeDocxViewer = useCallback(() => setDocxViewer(null), []);
    const closeSheetViewer = useCallback(() => setSheetViewer(null), []);
    const closeFileViewer = useCallback(() => setFileViewer((prev) => closeBlobViewer(prev)), []);

    // Revoke the current fileViewer blob URL on unmount (e.g. navigating away while a
    // viewer is open) — the on-close/re-open revokes above only fire on those explicit
    // paths, not on unmount. The ref always tracks the latest fileViewer so the cleanup
    // never revokes a URL that was already revoked by closeFileViewer.
    const fileViewerRef = useRef(fileViewer);
    fileViewerRef.current = fileViewer;
    useEffect(
        () => () => {
            if (fileViewerRef.current?.url) URL.revokeObjectURL(fileViewerRef.current.url);
        },
        [],
    );

    /** Marks any in-flight `openFile` as stale and clears `openingFileUrl` — call
     *  when AskAica closes so a fetch/parse that resolves afterward can't mount
     *  a viewer or leave the "opening" spinner stuck for the next open. */
    const invalidatePendingOpens = useCallback(() => {
        openGenerationRef.current += 1;
        setOpeningFileUrl(null);
    }, []);

    const openFile = useCallback(
        async (file: AskAicaFileRef) => {
            if (openingFileUrl) return; // an open is already in flight
            const generation = openGenerationRef.current;
            setOpeningFileUrl(file.url);
            try {
                const blob = await fetchAskAicaFileBlob(file.url, contextOrgId);
                if (generation !== openGenerationRef.current) return; // invalidated mid-fetch
                const mimeType = resolveMimeType(file);

                // DOCX_TYPES is shared with DocumentViewer/FileViewerDrawer (common/DocumentViewer,
                // out of scope here) so it can't gain txt without changing their routing too — txt
                // is added as a local OR, matching Data Room's own docx-or-txt branch
                // (useFetchDataRoomFiles.ts). DocxDrawer itself already converts txt.
                if (DOCX_TYPES.includes(mimeType) || mimeType === EXT_MIME.txt) {
                    setDocxViewer({ file, blob });
                    return;
                }
                if (SPREADSHEET_TYPES.includes(mimeType)) {
                    // This is opening an existing file for viewing, not importing a new
                    // one — labelled 'view' so load-failure telemetry doesn't lump it in
                    // with the generic 'import' default.
                    const sheets = await importFileToSheets(new File([blob], file.name, mimeType ? { type: mimeType } : undefined), {
                        context: 'view',
                    });
                    if (generation !== openGenerationRef.current) return; // invalidated mid-parse
                    const originalBytes = isReadOnlySize(countFilledCells(sheets)) ? await blob.arrayBuffer() : undefined;
                    if (generation !== openGenerationRef.current) return; // invalidated mid-read
                    setSheetViewer({ file, sheets, originalBytes });
                    return;
                }
                setFileViewer((prev) => {
                    closeBlobViewer(prev);
                    return { ...openBlobViewer(blob), file, mimeType };
                });
            } catch (err) {
                // Never log the raw error — an AxiosError's `config.headers` carries
                // the cerberus token (e.g. on a wrong-org 403).
                console.error('Failed to open Ask AICA file:', err instanceof Error ? err.message : 'unknown error');
                toast.error(sheetLoadErrorMessage(err, 'Unable to open this file. Please try again.'));
            } finally {
                if (isMountedRef.current && generation === openGenerationRef.current) setOpeningFileUrl(null);
            }
        },
        [contextOrgId, openingFileUrl],
    );

    /**
     * `payload` arrives from the Sheet/Docx drawer's header button: a
     * read-only-band workbook's bytes, or — when the user changed the file on
     * screen — that snapshot, which is what lands in Workspace.
     * Without it (card menu, or an untouched drawer) the ORIGINAL file is
     * re-fetched and snapshotted exactly as before.
     */
    const addToReports = useCallback(
        async (file: AskAicaFileRef, payload?: unknown) => {
            if (!contextOrgId) {
                toast.error('Missing organisation context — cannot add to Reports.');
                return;
            }
            if (addingToReportsRef.current) return;
            addingToReportsRef.current = true;
            setAddingToReports(true);
            // A fresh add must not read as succeeded off a previous add's lingering
            // "Added" state — the drawers key their close-after-add on this flag.
            setAddedToReports(false);
            try {
                const reportArgs = {
                    fileName: file.name,
                    assesseeOrgId: contextOrgId,
                    investorOrgId: userData?.investorOrgId,
                    createdBy: userData?.primaryEmail,
                };
                if (isReadOnlyAddBand(payload)) {
                    // Drawer-hosted read-only-band Add: the bytes are already in memory.
                    await addOriginalXlsxToReports({ ...payload, ...reportArgs });
                } else if (isEditedWorkspaceSnapshot(payload)) {
                    await addEditedFileToReports({ ...reportArgs, snapshot: payload });
                } else {
                    await addFileToReports({
                        ...reportArgs,
                        file: await fetchAskAicaFileBlob(file.url, contextOrgId),
                        fileType: file.type,
                    });
                }
                showAddedToReportsToast(
                    () => window.open(`/investor/app/reports/${contextOrgId}`, '_blank', 'noopener,noreferrer'),
                    () => {
                        if (isMountedRef.current) setAddedToReports(false);
                    },
                );
                setAddedToReports(true);
            } catch (err) {
                console.error('Failed to add Ask AICA file to Reports:', err instanceof Error ? err.message : 'unknown error');
                toast.error(sheetLoadErrorMessage(err, ADD_TO_REPORTS_FAILED_MESSAGE));
            } finally {
                addingToReportsRef.current = false;
                setAddingToReports(false);
            }
        },
        [contextOrgId, userData?.investorOrgId, userData?.primaryEmail],
    );

    return {
        openFile,
        /** Call when AskAica closes — invalidates any in-flight openFile. */
        invalidatePendingOpens,
        /** URL of the file currently being opened (fetch + parse in flight), or null. */
        openingFileUrl,
        addToReports: canAddToReports ? addToReports : undefined,
        addingToReports,
        addedToReports,
        docxViewer: docxViewer && {
            fileName: docxViewer.file.name,
            blob: docxViewer.blob,
            onClose: closeDocxViewer,
            onDownload: () => triggerBlobDownloadOnce(docxViewer.blob, docxViewer.file.name),
            onAddToReports: canAddToReports ? (payload?: AddToWorkspacePayload) => addToReports(docxViewer.file, payload) : undefined,
        },
        sheetViewer: sheetViewer && {
            fileName: sheetViewer.file.name,
            sheets: sheetViewer.sheets,
            originalBytes: sheetViewer.originalBytes,
            onClose: closeSheetViewer,
            onAddToReports: canAddToReports ? (payload?: AddToWorkspacePayload) => addToReports(sheetViewer.file, payload) : undefined,
        },
        fileViewer: fileViewer && {
            fileName: fileViewer.file.name,
            url: fileViewer.url,
            mimeType: fileViewer.mimeType,
            onClose: closeFileViewer,
            onDownload: () => triggerBlobDownload(fileViewer.url, fileViewer.file.name),
        },
    };
};

export default useAskAicaFileActions;
